/**
 * DiscTipsMiniCard — surfaces the current agent's personalised DISC tips
 * on the Call Detail page, filtered to the caller's DISC type.
 *
 * Lazy-loads from the cached endpoint (24h TTL) so this is essentially
 * free on the hot path. When the caller has no DISC yet, or the agent
 * hasn't generated tips, the component renders nothing.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = new Set([
  'master_admin', 'admin', 'manager', 'supervisor',
  'domain_admin', 'domain_supervisor',
]);
const DISC_COLOR = { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' };

export const DiscTipsMiniCard = ({ callId, callerPhone }) => {
  const { user } = useAuth();
  const [tips, setTips] = useState(null);
  const [callerDisc, setCallerDisc] = useState(null);

  const isAgent = !MANAGER_ROLES.has(user?.role);

  useEffect(() => {
    if (!isAgent || !callId) return;
    // 1. resolve the caller's DISC for this specific call
    axios.get(`${API}/analytics/caller-intel/${callId}/review`)
      .then((r) => setCallerDisc((r.data?.disc || '').toUpperCase() || null))
      .catch(() => setCallerDisc(null));
  }, [isAgent, callId]);

  useEffect(() => {
    // 2. once we know there IS a DISC, pull the agent's personalised tips
    if (!callerDisc) return;
    axios.get(`${API}/analytics/my-coaching/disc-tips`)
      .then((r) => setTips(r.data))
      .catch(() => setTips(null));
  }, [callerDisc]);

  if (!isAgent || !callerDisc || !tips || tips.empty) return null;

  const forThisDisc = (tips.tips_by_disc || {})[callerDisc];
  const hasOtherDiscs = Object.keys(tips.tips_by_disc || {}).length > 0;

  if (!forThisDisc || forThisDisc.length === 0) {
    // Agent has personalised tips but not for THIS caller's DISC — nudge
    // them to refresh in My Coaching so they don't sit blind on the call.
    if (!hasOtherDiscs) return null;
    return (
      <Card className="border-slate-200 bg-slate-50/60" data-testid="disc-tips-mini-card-empty">
        <CardContent className="p-3">
          <p className="text-xs text-slate-600">
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold mr-1.5 align-middle"
              style={{ background: DISC_COLOR[callerDisc] || '#94A3B8' }}
            >
              {callerDisc}
            </span>
            You don&apos;t have personalised tips for DISC · {callerDisc} yet.{' '}
            <a href="/dashboard?tab=my-coaching" className="font-semibold text-teal-700 hover:text-teal-900">
              Refresh in My Coaching →
            </a>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50/50 to-white" data-testid="disc-tips-mini-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
            style={{ background: DISC_COLOR[callerDisc] || '#94A3B8' }}
          >
            {callerDisc}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-teal-900">
              Your best moves for DISC · {callerDisc}
            </p>
            <p className="text-[10px] text-slate-500">
              Personalised from your own calls with {callerPhone || 'similar callers'}
            </p>
          </div>
          <Sparkles className="w-4 h-4 text-teal-600 shrink-0" />
        </div>
        <ul className="space-y-1.5">
          {forThisDisc.slice(0, 3).map((tip, i) => (
            <li
              key={i}
              className="text-[11px] text-slate-700 leading-snug pl-3 border-l-2 border-teal-200"
              data-testid={`disc-mini-tip-${i}`}
            >
              {tip}
            </li>
          ))}
        </ul>
        <a
          href="/dashboard?tab=my-coaching"
          className="inline-flex items-center gap-1 mt-3 text-[10px] font-semibold text-teal-700 hover:text-teal-900"
        >
          Refresh in My Coaching <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </CardContent>
    </Card>
  );
};

export default DiscTipsMiniCard;
